// Unpublished Work © 2023-2024 Deere & Company.
import { AxiosResponse } from "axios";
import { BlobResponse } from "../../utils/downloadFile";
import axios from "../api";
import {
  ConsolidatedInvoicesExcelRequest,
  DealerTimeLimitInvoiceLineItem,
  EXCEL_MIME_TYPE,
  IInvoiceApi,
  Invoice,
  InvoiceHeaderDetails,
  InvoiceHeaderDetailsV2,
  InvoiceLineItem,
  InvoicesResponse,
  LineItem,
} from "./interfaces";

const InvoiceApi: IInvoiceApi = {
  getInvoicePDF: async (
    invoiceServiceUrl: string,
    invoiceNumber: string,
    accountId: string,
    token: string,
    objTypeCode?: string | null,
    location?: string,
    lang?: string,
    fileName?: string | null,
    mru?: string | null
  ): Promise<AxiosResponse<Blob>> =>
    axios
      .getWithRetries(`${invoiceServiceUrl}/v1/invoices/${invoiceNumber}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "account-id": accountId,
          Accept: "application/pdf",
        },
        responseType: "blob",
        params: {
          objTypeCode,
          fileName,
          lang,
          mru,
          location,
        },
      })
      .then((response) => response),
  getInvoiceAttachment: async (
    invoiceServiceUrl: string,
    invoiceNumber: string,
    token: string,
    fileName: string | null,
    officialDocumentNumber: string | null,
    accountId: string,
    lang?: string
  ): Promise<AxiosResponse<Blob>> =>
    axios
      .getWithRetries(
        `${invoiceServiceUrl}/v1/invoices/${invoiceNumber}/attachment`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "account-id": accountId,
          },
          responseType: "blob",
          params: {
            fileName,
            officialDocumentNumber,
            lang,
          },
        }
      )
      .then((response) => response),
  getInvoice: async (
    invoiceServiceUrl: string,
    invoiceNumber: string,
    accountId: string,
    token: string,
    location?: string,
    lang?: string,
    lineItemPartDescTranslationEnabled?: boolean
  ): Promise<Invoice> => {
    const invoiceHeaderResponse = await axios
      .getWithRetries(`${invoiceServiceUrl}/v1/invoices/${invoiceNumber}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "account-id": accountId,
        },
        params: {
          location,
          lang,
        },
      })
      .then((response) => response.data);

    const invoiceLineItemsResponse = await axios
      .getWithRetries(
        `${invoiceServiceUrl}/v1/invoices/${invoiceNumber}/line-items`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(lineItemPartDescTranslationEnabled
              ? { "account-id": accountId || "" }
              : {}),
          },
          params: {
            lang,
          },
        }
      )
      .then((response) =>
        response.data.lineItems
          .map(
            (lineItem: InvoiceLineItem) =>
              ({
                serialNumber: lineItem.lineNumber,
                invoicedPart: lineItem.partNumber,
                partDesc: lineItem.description,
                billingQuantity: lineItem.quantity,
              } as LineItem)
          )
          .sort(
            (lineItemA: LineItem, lineItemB: LineItem) =>
              lineItemA.serialNumber - lineItemB.serialNumber
          )
      );

    return {
      invoiceNumber: invoiceHeaderResponse.invoiceNumber,
      nfeNumber: invoiceHeaderResponse.nfeNumber,
      lineItems: invoiceLineItemsResponse,
      billingDate: invoiceHeaderResponse.invoiceDate,
    } as Invoice;
  },
  getInvoiceWithTimeLimit: async (
    invoiceServiceUrl: string,
    invoiceNumber: string,
    accountId: string,
    token: string,
    transactionType: string,
    location?: string,
    lang?: string
  ): Promise<Invoice> => {
    const invoiceHeaderResponse = await axios
      .getWithRetries(`${invoiceServiceUrl}/v2/invoices/${invoiceNumber}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "account-id": accountId,
        },
        params: {
          location,
          lang,
        },
      })
      .then((response) => response.data);

    const invoiceLineItemsResponse = await axios
      .getWithRetries(
        `${invoiceServiceUrl}/v2/invoices/${invoiceNumber}/line-items`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "account-id": accountId,
          },
          params: {
            transactionType,
            location,
          },
        }
      )
      .then((response) =>
        response.data.lineItems
          .map(
            (lineItem: InvoiceLineItem) =>
              ({
                serialNumber: lineItem.lineNumber,
                invoicedPart: lineItem.partNumber,
                partDesc: lineItem.description,
                billingQuantity: lineItem.quantity,
                timeLimitExceeded: lineItem.timeLimitExceeded === "true",
              } as LineItem)
          )
          .sort(
            (lineItemA: LineItem, lineItemB: LineItem) =>
              lineItemA.serialNumber - lineItemB.serialNumber
          )
      );
    return {
      invoiceNumber: invoiceHeaderResponse.invoiceNumber,
      nfeNumber: invoiceHeaderResponse.nfeNumber,
      timeLimitExceeded: invoiceHeaderResponse.timeLimit?.returnsExceeded,
      lineItems: invoiceLineItemsResponse,
    } as Invoice;
  },
  getMultiInvoiceTimeLimitForLineItems: async (
    invoiceServiceUrl: string,
    accountId: string,
    token: string,
    transactionType: string,
    invoiceNumbers: string
  ): Promise<DealerTimeLimitInvoiceLineItem[]> => {
    const dealerTimeLimitForMultiInvoiceResponse = await axios
      .getWithRetries(`${invoiceServiceUrl}/v2/invoices/time-limit`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "account-id": accountId,
        },
        params: {
          invoiceNumbers,
          transactionType,
        },
      })
      .then((response) => response.data);

    return dealerTimeLimitForMultiInvoiceResponse;
  },

  downloadInvoices: async (
    invoiceServiceUrl: string,
    accountId: string,
    jobId: string,
    token: string
  ): Promise<AxiosResponse<Blob>> =>
    axios
      .getWithRetries(
        `${invoiceServiceUrl}/v1/invoices/download-consolidated/${jobId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "account-id": accountId,
          },
          responseType: "blob",
        }
      )
      .then((response) => response),
  getInvoices: async (
    invoiceServiceUrl: string,
    accountId: string,
    token: string,
    lang?: string,
    pageSize?: number,
    pageNumber?: number,
    startDate?: string,
    endDate?: string,
    orderBy?: string,
    sortBy?: string,
    invoiceNumber?: string,
    documentNumber?: string,
    dealerReference?: string,
    invoiceType?: string,
    partNumber?: string,
    huNumber?: string,
    nfeNumber?: string,
    officialDocumentNumber?: string,
    withHandlingUnits?: boolean,
    withLeadingZeros?: boolean,
    dealerIdFilter?: string,
    factura?: string,
    mru?: string,
    country?: string,
    srcProcessType?: string
  ): Promise<InvoicesResponse> => {
    const response = await axios.getWithRetries(
      `${invoiceServiceUrl}/v1/invoices`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "account-id": accountId,
        },
        params: {
          startDate,
          endDate,
          orderBy,
          sortBy,
          pageSize,
          pageNumber,
          lang,
          invoiceNumber,
          documentNumber,
          dealerReference,
          invoiceType,
          partNumber,
          huNumber,
          nfeNumber,
          officialDocumentNumber,
          withHandlingUnits,
          withLeadingZeros,
          dealerIdFilter,
          factura,
          mru,
          country,
          srcProcessType,
        },
      }
    );

    return {
      ...response.data,
      invoices: response.data.invoices.map((invoice: any) => ({
        ...invoice,
        documents: invoice.documents.flatMap((document: any) => {
          if (document.handlingUnits.length === 0) {
            return {
              documentNumber: document.documentNumber,
              dealerReference: document.dealerReference,
              orderType: document.orderType,
            };
          }

          return document.handlingUnits.map((handlingUnit: string) => ({
            documentNumber: document.documentNumber,
            dealerReference: document.dealerReference,
            orderType: document.orderType,
            handlingUnit,
          }));
        }),
      })),
    };
  },
  invoiceViewed: async (
    invoiceServiceUrl: string,
    invoiceNumber: string | undefined,
    dealerId: string,
    seenBy: string,
    country: string | undefined,
    accessToken: string | undefined,
    lang: string
  ): Promise<void> => {
    await axios.patchWithRetries(
      `${invoiceServiceUrl}/v1/invoices/${invoiceNumber}?lang=${lang}`,
      {
        seenBy,
        country,
      },
      {
        headers: {
          "account-id": dealerId,
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
  },
  getInvoiceDetails: async (
    invoiceServiceUrl: string,
    invoiceNumber: string,
    token: string,
    accountId: string,
    mru: string,
    location?: string,
    lang?: string,
    withLeadingZeros?: boolean,
    portlandSurchargesEnabled?: boolean
  ): Promise<InvoiceHeaderDetails> =>
    axios
      .getWithRetries(`${invoiceServiceUrl}/v1/invoices/${invoiceNumber}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "account-id": accountId,
        },
        params: {
          location,
          lang,
          withLeadingZeros,
          portlandSurchargesEnabled,
          mru,
        },
      })
      .then((response) => ({
        ...response.data,
        shipToAddress: {
          addressLine1: response.data.shipToAddress?.name,
          addressLine2: response.data.shipToAddress?.street,
          cityName: response.data.shipToAddress?.city,
          stateOrProvince: response.data.shipToAddress?.region,
          countryCode: response.data.shipToAddress?.country,
          postalOrZipCode: response.data.shipToAddress?.postCode,
          phoneNumber: response.data.shipToAddress?.telNumber,
        },
        soldToAddress: {
          addressLine1: response.data.soldToAddress?.name,
          addressLine2: response.data.soldToAddress?.street,
          cityName: response.data.soldToAddress?.city,
          stateOrProvince: response.data.soldToAddress?.region,
          countryCode: response.data.soldToAddress?.country,
          postalOrZipCode: response.data.soldToAddress?.postCode,
          phoneNumber: response.data.soldToAddress?.telNumber,
        },
        shippingInformation: {
          addressLine1: response.data.shippingInformation?.name,
          addressLine2: response.data.shippingInformation?.street,
          cityName: response.data.shippingInformation?.city,
          stateOrProvince: response.data.shippingInformation?.region,
          countryCode: response.data.shippingInformation?.country,
          postalOrZipCode: response.data.shippingInformation?.postCode,
          phoneNumber: response.data.shippingInformation?.telNumber,
        },
        invoiceDate: response.data.invoiceDate,
      })),
  getInvoiceDetailsV2: async (
    invoiceServiceUrl: string,
    invoiceNumber: string,
    token: string,
    accountId: string,
    mru: string,
    location?: string,
    lang?: string,
    withLeadingZeros?: boolean,
    portlandSurchargesEnabled?: boolean
  ): Promise<InvoiceHeaderDetailsV2> =>
    axios
      .getWithRetries(`${invoiceServiceUrl}/v2/invoices/${invoiceNumber}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "account-id": accountId,
        },
        params: {
          location,
          lang,
          withLeadingZeros,
          portlandSurchargesEnabled,
          mru,
        },
      })
      .then((response) => ({
        ...response.data,
        shipToAddress: {
          addressLine1: response.data.shipToAddress?.name,
          addressLine2: response.data.shipToAddress?.street,
          cityName: response.data.shipToAddress?.city,
          stateOrProvince: response.data.shipToAddress?.region,
          countryCode: response.data.shipToAddress?.country,
          postalOrZipCode: response.data.shipToAddress?.postCode,
          phoneNumber: response.data.shipToAddress?.telNumber,
        },
        soldToAddress: {
          addressLine1: response.data.soldToAddress?.name,
          addressLine2: response.data.soldToAddress?.street,
          cityName: response.data.soldToAddress?.city,
          stateOrProvince: response.data.soldToAddress?.region,
          countryCode: response.data.soldToAddress?.country,
          postalOrZipCode: response.data.soldToAddress?.postCode,
          phoneNumber: response.data.soldToAddress?.telNumber,
        },
        shippingInformation: {
          addressLine1: response.data.shippingInformation?.name,
          addressLine2: response.data.shippingInformation?.street,
          cityName: response.data.shippingInformation?.city,
          stateOrProvince: response.data.shippingInformation?.region,
          countryCode: response.data.shippingInformation?.country,
          postalOrZipCode: response.data.shippingInformation?.postCode,
          phoneNumber: response.data.shippingInformation?.telNumber,
        },
        invoiceDate: response.data.invoiceDate,
      })),
  getInvoiceLineItems: async (
    invoiceServiceUrl: string,
    invoiceNumber: string,
    token: string,
    location?: string,
    lang?: string,
    accountId?: string,
    lineItemPartDescTranslationEnabled?: boolean,
    portlandSurchargesEnabled?: boolean
  ): Promise<InvoiceLineItem[]> =>
    axios
      .getWithRetries(
        `${invoiceServiceUrl}/v1/invoices/${invoiceNumber}/line-items`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(lineItemPartDescTranslationEnabled
              ? { "account-id": accountId || "" }
              : {}),
          },
          params: {
            location,
            lang,
            portlandSurchargesEnabled,
          },
        }
      )
      .then((res) =>
        res.data.lineItems.map((lineItem: InvoiceLineItem) => ({
          ...lineItem,
        }))
      ),
  getDealerTimeLimitForInvoiceLineItem: async (
    invoiceServiceUrl: string,
    accountId: string,
    transactionType: string,
    invoiceNumber: string,
    invoiceLineNumber: string,
    token: string,
    returnReason?: string,
    lang?: string
  ): Promise<DealerTimeLimitInvoiceLineItem> =>
    axios
      .getWithRetries(
        `${invoiceServiceUrl}/v1/invoices/line-item/${invoiceLineNumber}/time-limit`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "account-id": accountId,
          },
          params: {
            transactionType,
            invoiceNumber,
            invoiceLineNumber,
            returnReason,
            lang,
          },
        }
      )
      .then((response) => ({
        ...response.data,
      })),
  getInvoicesLineItemsAsExcel: async (
    invoiceServiceUrl: string,
    accountId: string,
    invoiceNumber: string,
    token: string,
    invoiceType: string,
    dealerLocation: string,
    originalInvoiceNumber?: string,
    nfe?: string,
    originalNfe?: string
  ): Promise<AxiosResponse<BlobResponse>> =>
    axios.getWithRetries(
      `${invoiceServiceUrl}/v1/invoices/${invoiceNumber}/line-items/download/excel`,
      {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
          "account-id": accountId,
          Accept: EXCEL_MIME_TYPE,
        },
        params: {
          invoiceType,
          dealerLocation,
          originalInvoiceNumber,
          nfe,
          originalNfe,
        },
      }
    ),
  getConsolidatedInvoicesLineItemsAsExcel: async (
    invoiceServiceUrl: string,
    request: ConsolidatedInvoicesExcelRequest,
    token: string
  ): Promise<AxiosResponse<BlobResponse>> =>
    axios.postWithRetries(
      `${invoiceServiceUrl}/v1/invoices/consolidated/line-items/download/excel`,
      request,
      {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: EXCEL_MIME_TYPE,
        },
      }
    ),
  getInvoicesByDocumentNumber: async (
    invoiceServiceUrl: string,
    accountId: string,
    token: string,
    documentNumber: string,
    invoiceType?: string
  ): Promise<InvoicesResponse> => {
    // Build a 6-month lookback window so the API returns recent invoices
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 6);
    const fmt = (d: Date) => d.toISOString().slice(0, 10); // YYYY-MM-DD

    const response = await axios.getWithRetries(
      `${invoiceServiceUrl}/v1/invoices`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "account-id": accountId,
        },
        params: {
          documentNumber,
          invoiceType,
          startDate: fmt(startDate),
          endDate: fmt(endDate),
          orderBy: "ASC",
        },
      }
    );

    return {
      ...response.data,
      invoices: (response.data.invoices ?? []).map((invoice: any) => ({
        ...invoice,
        documents: (invoice.documents ?? []).flatMap((document: any) => {
          if (!document.handlingUnits || document.handlingUnits.length === 0) {
            return {
              documentNumber: document.documentNumber,
              dealerReference: document.dealerReference,
            };
          }

          return document.handlingUnits.map((handlingUnit: string) => ({
            documentNumber: document.documentNumber,
            dealerReference: document.dealerReference,
            handlingUnit,
          }));
        }),
      })),
    };
  },
};

export default InvoiceApi;
